import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { AsignadoAvatar } from './utils/displays';

// Cache global de resoluciones por codigo, para no re-fetchear constantemente
const _cache = new Map();
const _pending = new Map();

async function resolveByCode(code) {
  if (_cache.has(code)) return _cache.get(code);
  if (_pending.has(code)) return _pending.get(code);

  const [slug, numStr] = code.split('-');
  const numero = parseInt(numStr, 10);
  if (!slug || isNaN(numero)) return null;

  const p = (async () => {
    try {
      // Buscar primero el proyecto por slug para obtener id
      const { data: proyecto } = await axiosInstance.get(`/api/proyectos/${slug}`);
      // Despues listar tareas del proyecto y filtrar por numero
      const { data: tareas } = await axiosInstance.get(`/api/proyectos/${proyecto.id}/tareas`);
      const tarea = (tareas || []).find((t) => t.numero === numero);
      if (!tarea) return null;
      const result = { tarea, proyecto };
      _cache.set(code, result);
      return result;
    } catch (err) {
      _cache.set(code, null);
      return null;
    } finally {
      _pending.delete(code);
    }
  })();
  _pending.set(code, p);
  return p;
}

/**
 * Chip rich inline para una mencion #SLUG-NUM en un mensaje.
 * Carga la tarea on-demand y muestra: codigo (mono) + titulo + estado dot.
 * Click navega al slide-over de la tarea.
 */
const TaskMentionChip = ({ codigo }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(_cache.get(codigo));
  const [hover, setHover] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!info) {
      resolveByCode(codigo).then((r) => {
        if (mounted.current) setInfo(r);
      });
    }
    return () => { mounted.current = false; };
  }, [codigo, info]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (info?.tarea && info?.proyecto) {
      navigate(`/proyectos/${info.proyecto.slug}/tareas/${info.tarea.numero}`);
    }
  };

  // Estado de carga / no encontrado: render minimal
  if (info === null) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded font-mono text-[10px] tracking-widest uppercase text-slate-400 bg-slate-100 align-baseline"
        title="Tarea no encontrada o sin acceso"
      >
        {codigo}
      </span>
    );
  }
  if (!info) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded font-mono text-[10px] tracking-widest uppercase text-cyan-700 bg-cyan-50 align-baseline">
        {codigo}
      </span>
    );
  }

  const { tarea, proyecto } = info;
  const estadoColor = tarea.estado?.color || '#94a3b8';
  const proyectoColor = proyecto.color || '#06b6d4';
  const asignados = tarea.asignados || [];
  const primerAsignado = asignados[0] || null;
  const asignadosExtra = asignados.length - 1;

  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-[11px] bg-white border border-cyan-200 text-slate-800 hover:bg-cyan-50 hover:border-cyan-300 transition-colors align-baseline"
      >
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: proyectoColor }}
        />
        <span className="font-mono text-[9px] tracking-widest uppercase text-slate-500">
          {codigo}
        </span>
        <span className="text-[10px] text-slate-700 truncate max-w-[160px]">
          {tarea.titulo}
        </span>
      </button>

      {/* Tooltip al hover con detalle */}
      {hover && (
        <span
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-40 w-64 bg-slate-900 text-white rounded-lg shadow-xl px-3 py-2 pointer-events-none"
          style={{ animation: 'fadeIn 120ms ease-out' }}
        >
          <span className="block text-[9px] tracking-widest uppercase text-slate-400 font-mono mb-0.5">
            {proyecto.nombre} · {codigo}
          </span>
          <span className="block text-xs font-medium text-white leading-snug mb-1.5">
            {tarea.titulo}
          </span>
          <span className="flex items-center gap-2 text-[10px]">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium"
              style={{
                backgroundColor: `${estadoColor}33`,
                color: '#fff',
              }}
            >
              <span className="w-1 h-1 rounded-full" style={{ backgroundColor: estadoColor }} />
              {tarea.estado?.nombre || '—'}
            </span>
            {primerAsignado && (
              <span className="inline-flex items-center gap-1 text-slate-300 truncate">
                <AsignadoAvatar nombre={primerAsignado.nombre} color={proyectoColor} size={16} />
                <span className="truncate">{primerAsignado.nombre}</span>
                {asignadosExtra > 0 && (
                  <span className="text-slate-400 tabular-nums">+{asignadosExtra}</span>
                )}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
};

export default TaskMentionChip;
